import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode
} from "react";
import { createPortal } from "react-dom";

export type NotificationKind = "info" | "success" | "warning" | "error";
export type NotificationPlacement = "floating" | "inline";

export interface NotificationOptions {
  title: string;
  message?: string;
  timeoutMs?: number;
  persist?: boolean;
  placement?: NotificationPlacement;
  dedupeKey?: string;
}

interface ToastRecord extends NotificationOptions {
  id: string;
  kind: NotificationKind;
  visible: boolean;
}

interface NotificationApi {
  info(options: NotificationOptions): string;
  success(options: NotificationOptions): string;
  warning(options: NotificationOptions): string;
  error(options: NotificationOptions): string;
  dismiss(id: string): void;
  clear(): void;
}

interface NotificationContextValue extends NotificationApi {
  toasts: ToastRecord[];
}

const noopNotify = () => "";

const NotificationContext = createContext<NotificationContextValue>({
  info: noopNotify,
  success: noopNotify,
  warning: noopNotify,
  error: noopNotify,
  dismiss: () => undefined,
  clear: () => undefined,
  toasts: []
});

const DEFAULT_TIMEOUT_MS: Record<NotificationKind, number> = {
  info: 4500,
  success: 5200,
  warning: 7000,
  error: 0
};

const MAX_STANDARD_TOAST_QUEUE = 6;
const MAX_FLOATING_TOASTS = 3;
const TOAST_EXIT_MS = 220;
const FLOATING_TOAST_ROOT_ATTR = "data-tcs-floating-toasts";

let toastSequence = 0;

const isFailureToast = (toast: Pick<ToastRecord, "kind">): boolean =>
  toast.kind === "error";

const enforceToastRetention = (
  toasts: readonly ToastRecord[]
): ToastRecord[] => {
  let standardCount = 0;

  return toasts.filter((toast) => {
    if (isFailureToast(toast)) {
      return true;
    }

    standardCount += 1;
    return standardCount <= MAX_STANDARD_TOAST_QUEUE;
  });
};

const floatingToastPreview = (
  toasts: readonly ToastRecord[]
): ToastRecord[] => {
  const failures = toasts.filter(isFailureToast);
  const standard = toasts.filter((toast) => !isFailureToast(toast));

  return [...failures, ...standard].slice(0, MAX_FLOATING_TOASTS);
};

export function NotificationProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastRecord[]>([]);
  const mountedRef = useRef(true);
  const timeoutHandlesRef = useRef(new Set<ReturnType<typeof globalThis.setTimeout>>());
  const frameHandlesRef = useRef(
    new Set<ReturnType<typeof globalThis.requestAnimationFrame>>()
  );

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;

      for (const timeoutHandle of timeoutHandlesRef.current) {
        globalThis.clearTimeout(timeoutHandle);
      }
      timeoutHandlesRef.current.clear();

      if (typeof globalThis.cancelAnimationFrame === "function") {
        for (const frameHandle of frameHandlesRef.current) {
          globalThis.cancelAnimationFrame(frameHandle);
        }
      }
      frameHandlesRef.current.clear();
    };
  }, []);

  const scheduleTimeout = useCallback((callback: () => void, delay: number) => {
    const timeoutHandles = timeoutHandlesRef.current;
    const timeoutHandle = globalThis.setTimeout(() => {
      timeoutHandles.delete(timeoutHandle);

      if (!mountedRef.current) {
        return;
      }

      callback();
    }, delay);

    timeoutHandles.add(timeoutHandle);
    return timeoutHandle;
  }, []);

  const scheduleAnimationFrame = useCallback(
    (callback: FrameRequestCallback) => {
      if (typeof globalThis.requestAnimationFrame !== "function") {
        scheduleTimeout(() => callback(globalThis.performance.now()), 0);
        return;
      }

      const frameHandles = frameHandlesRef.current;
      let ranSynchronously = false;
      let frameHandle: ReturnType<typeof globalThis.requestAnimationFrame> | undefined;

      frameHandle = globalThis.requestAnimationFrame((timestamp) => {
        if (frameHandle === undefined) {
          ranSynchronously = true;
        } else {
          frameHandles.delete(frameHandle);
        }

        if (!mountedRef.current) {
          return;
        }

        callback(timestamp);
      });

      if (!ranSynchronously) {
        frameHandles.add(frameHandle);
      }
    },
    [scheduleTimeout]
  );

  const dismiss = useCallback((id: string) => {
    setToasts((current) =>
      current.map((toast) => (toast.id === id ? { ...toast, visible: false } : toast))
    );
    scheduleTimeout(() => {
      setToasts((current) => current.filter((toast) => toast.id !== id));
    }, TOAST_EXIT_MS);
  }, [scheduleTimeout]);

  const clear = useCallback(() => {
    setToasts((current) => current.map((toast) => ({ ...toast, visible: false })));
    scheduleTimeout(() => {
      setToasts([]);
    }, TOAST_EXIT_MS);
  }, [scheduleTimeout]);

  const addToast = useCallback(
    (kind: NotificationKind, options: NotificationOptions) => {
      const id = `toast-${Date.now()}-${toastSequence++}`;
      const timeoutMs =
        options.timeoutMs ?? (options.persist ? 0 : DEFAULT_TIMEOUT_MS[kind]);
      const toast: ToastRecord = {
        ...options,
        id,
        kind,
        timeoutMs,
        persist: options.persist ?? timeoutMs === 0,
        visible: false
      };

      setToasts((current) => {
        const deduped = toast.dedupeKey
          ? current.filter((candidate) => candidate.dedupeKey !== toast.dedupeKey)
          : current;

        return enforceToastRetention([toast, ...deduped]);
      });
      scheduleAnimationFrame(() => {
        setToasts((current) =>
          current.map((candidate) =>
            candidate.id === id ? { ...candidate, visible: true } : candidate
          )
        );
      });
      if (timeoutMs > 0) {
        scheduleTimeout(() => dismiss(id), timeoutMs);
      }

      return id;
    },
    [dismiss, scheduleAnimationFrame, scheduleTimeout]
  );

  const api = useMemo<NotificationContextValue>(
    () => ({
      info: (options) => addToast("info", options),
      success: (options) => addToast("success", options),
      warning: (options) => addToast("warning", options),
      error: (options) => addToast("error", { persist: true, ...options }),
      dismiss,
      clear,
      toasts
    }),
    [addToast, clear, dismiss, toasts]
  );

  return (
    <NotificationContext.Provider value={api}>{children}</NotificationContext.Provider>
  );
}

export const useNotify = (): NotificationApi => useContext(NotificationContext);

export function ToastViewport({
  placement = "floating"
}: {
  placement?: NotificationPlacement;
}) {
  const { toasts, dismiss } = useContext(NotificationContext);
  const [expanded, setExpanded] = useState(false);
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);
  const placementToasts = toasts.filter(
    (toast) => (toast.placement ?? "floating") === placement
  );
  const visibleToasts =
    placement === "floating"
      ? expanded
        ? placementToasts
        : floatingToastPreview(placementToasts)
      : placementToasts;
  const hiddenCount =
    placement === "floating"
      ? Math.max(placementToasts.length - visibleToasts.length, 0)
      : 0;

  useEffect(() => {
    if (
      placement === "floating" &&
      expanded &&
      placementToasts.length <= MAX_FLOATING_TOASTS
    ) {
      setExpanded(false);
    }
  }, [expanded, placement, placementToasts.length]);

  useEffect(() => {
    if (placement !== "floating" || typeof document === "undefined") {
      return;
    }

    setPortalTarget(document.body);
  }, [placement]);

  const stack = (
    <ToastStack
      expanded={expanded}
      hiddenCount={hiddenCount}
      onDismiss={dismiss}
      onExpandedChange={setExpanded}
      placement={placement}
      toasts={visibleToasts}
    />
  );

  if (placement !== "floating") {
    return stack;
  }

  return portalTarget ? createPortal(stack, portalTarget) : null;
}

function ToastStack({
  expanded,
  hiddenCount,
  onExpandedChange,
  placement,
  toasts,
  onDismiss
}: {
  expanded: boolean;
  hiddenCount: number;
  onExpandedChange: (expanded: boolean) => void;
  placement: NotificationPlacement;
  toasts: readonly ToastRecord[];
  onDismiss: (id: string) => void;
}) {
  useEffect(() => {
    if (placement !== "floating" || typeof document === "undefined") {
      return undefined;
    }

    const root = document.documentElement;

    if (toasts.length > 0 || hiddenCount > 0) {
      root.setAttribute(FLOATING_TOAST_ROOT_ATTR, "present");
    } else {
      root.removeAttribute(FLOATING_TOAST_ROOT_ATTR);
    }

    return () => {
      root.removeAttribute(FLOATING_TOAST_ROOT_ATTR);
    };
  }, [hiddenCount, placement, toasts.length]);

  if (toasts.length === 0) {
    return null;
  }

  return (
    <div
      aria-live="polite"
      aria-atomic="false"
      data-tcs-toast-viewport={placement}
      data-tcs-toast-portal={placement === "floating" ? "body" : undefined}
      className={
        placement === "inline"
          ? "grid w-full gap-2"
          : "tcsToastViewport pointer-events-none fixed right-3 top-3 z-[100] grid w-[min(420px,calc(100vw-24px))] gap-2 sm:right-4 sm:top-4"
      }
    >
      {toasts.map((toast) => (
        <Toast key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
      {hiddenCount > 0 ? (
        <button
          type="button"
          aria-expanded={expanded}
          onClick={() => onExpandedChange(true)}
          className="pointer-events-auto rounded-[8px] border border-[var(--line)] bg-[var(--raised)] px-3 py-2 text-left text-[12px] font-extrabold text-[var(--ink)] shadow-[0_10px_30px_rgba(22,24,29,.10)] hover:bg-[var(--chip)]"
        >
          +{hiddenCount} more notification{hiddenCount === 1 ? "" : "s"}
        </button>
      ) : expanded ? (
        <button
          type="button"
          aria-expanded="true"
          onClick={() => onExpandedChange(false)}
          className="pointer-events-auto justify-self-start rounded-[8px] border border-[var(--line)] bg-[var(--raised)] px-3 py-2 text-[12px] font-extrabold text-[var(--muted)] hover:bg-[var(--chip)]"
        >
          Show fewer notifications
        </button>
      ) : null}
    </div>
  );
}

function Toast({
  toast,
  onDismiss
}: {
  toast: ToastRecord;
  onDismiss: (id: string) => void;
}) {
  const styles = toastStyles[toast.kind];

  return (
    <section
      role={toast.kind === "error" ? "alert" : "status"}
      className={[
        "tcsToastMotion pointer-events-none rounded-[8px] border bg-[var(--surface)] px-4 py-3 shadow-[0_18px_48px_rgba(22,24,29,.16)] transition-[opacity,transform] duration-200 ease-out",
        toast.visible ? "tcsToastEnter" : "tcsToastExit",
        styles.border
      ].join(" ")}
    >
      <div className="flex items-start gap-3">
        <span
          aria-hidden="true"
          className={["mt-0.5 h-2.5 w-2.5 shrink-0 rounded-full", styles.dot].join(" ")}
        />
        <div className="min-w-0 flex-1">
          <h2 className={["text-[13px] font-extrabold", styles.title].join(" ")}>
            {toast.title}
          </h2>
          {toast.message ? (
            <p className="mt-1 text-[12px] font-semibold leading-[1.45] text-[var(--muted)]">
              {toast.message}
            </p>
          ) : null}
        </div>
        <button
          type="button"
          aria-label={`Dismiss ${toast.title}`}
          onClick={() => onDismiss(toast.id)}
          className="pointer-events-auto grid h-7 w-7 shrink-0 cursor-pointer place-items-center rounded-[8px] border border-[var(--line)] bg-[var(--raised)] text-[16px] leading-none text-[var(--muted)] hover:bg-[var(--chip)]"
        >
          ×
        </button>
      </div>
    </section>
  );
}

const toastStyles: Record<
  NotificationKind,
  { border: string; dot: string; title: string }
> = {
  info: {
    border: "border-[#B8C8D7]",
    dot: "bg-[#3E6E9A]",
    title: "text-[var(--info)]"
  },
  success: {
    border: "border-[#B7D4C1]",
    dot: "bg-[#2E7B4F]",
    title: "text-[var(--success)]"
  },
  warning: {
    border: "border-[#E7D09A]",
    dot: "bg-[#A87518]",
    title: "text-[var(--warn)]"
  },
  error: {
    border: "border-[#E8B7AF]",
    dot: "bg-[#BC4A3C]",
    title: "text-[var(--risk)]"
  }
};
